import { onDocumentReady, yieldToMainThread } from '@theme/utilities';
import { loadScript, whenDeepIdle } from '@theme/third-party';

/**
 * Growave wishlist, loaded on our terms.
 *
 * The app embed boots every enabled Growave app at DOMContentLoaded and injects a widget into
 * each product card. We suppress its loader (see the GW_DISABLED cookie in scripts.liquid),
 * load the same bundle at idle, and init it with the wishlist app alone. The buttons are the
 * theme's own — the SDK only supplies state and mutations.
 */

const TOGGLE_SELECTOR = '[data-wishlist-toggle]';
const COUNT_SELECTOR = '[data-wishlist-count]';
// Growave's own page widgets, pasted into the page body in admin. Where one of these exists the
// SDK is the page's content, not an enhancement, so it cannot wait for idle.
const PAGE_WIDGET_SELECTOR = '.gw-wl-page-placeholder, .gw-wl-shared-page-placeholder, .gw-wl-widget-placeholder';
// The embed's inline loader carries the content-hashed bundle URL. Reading it back beats
// pinning a hash that goes stale on Growave's next release.
const BUNDLE_URL_PATTERN = /GW_BUNDLE_URL\s*=\s*["']([^"']+)["']/;
const SDK_READY_EVENT = 'gwSdkReady';
const SDK_READY_TIMEOUT = 15000;

/** Product ids currently in the wishlist. @type {Set<number>} */
const savedProductIds = new Set();
/** Accessible name each button carries while unpressed. @type {WeakMap<HTMLElement, string>} */
const unpressedLabels = new WeakMap();

/** @type {Promise<boolean> | null} */
let bootPromise = null;
/** @type {Promise<void> | null} */
let hydratePromise = null;
let isWatching = false;

/**
 * Pulls the Growave bundle URL out of the app embed's inline loader.
 * @returns {string | null}
 */
function findBundleUrl() {
  for (const script of document.querySelectorAll('script:not([src])')) {
    const match = script.textContent?.match(BUNDLE_URL_PATTERN);
    if (match) return match[1] ?? null;
  }
  return null;
}

/**
 * Reads the config the app embed renders, then renames the element.
 *
 * The bundle self-initialises off `#gw-additional-info` with every app the shop has enabled —
 * it would boot rewards and Instagram before our init() could narrow it. Taking the id away
 * leaves it waiting for init(), which we call with the same values and the wishlist alone.
 *
 * @returns {Record<string, any> | null}
 */
function takeEmbedConfig() {
  const element = document.querySelector('#gw-additional-info');
  if (!element?.textContent) return null;

  try {
    const config = JSON.parse(element.textContent).FEATURES ?? null;
    if (config) element.id = 'gw-additional-info-theme';
    return config;
  } catch {
    return null;
  }
}

/**
 * Resolves once the bundle has published `window.gw.sdk`.
 * @returns {Promise<any>}
 */
function whenSdkReady() {
  if (window.gw?.sdk) return Promise.resolve(window.gw.sdk);

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      document.removeEventListener(SDK_READY_EVENT, onReady);
      reject(new Error('Growave SDK never signalled ready'));
    }, SDK_READY_TIMEOUT);

    const onReady = () => {
      clearTimeout(timeout);
      resolve(window.gw.sdk);
    };

    document.addEventListener(SDK_READY_EVENT, onReady, { once: true });
  });
}

/**
 * Resolves once the wishlist module's lazy chunk has finished loading.
 * @param {any} sdk
 * @returns {Promise<void>}
 */
function whenWishlistsReady(sdk) {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Growave wishlist module never became ready')), SDK_READY_TIMEOUT);
    sdk.runIfModuleReady(sdk.common.constants.modules.WISHLISTS, () => {
      clearTimeout(timeout);
      resolve();
    });
  });
}

/**
 * Loads and initialises the SDK with the wishlist app only. Safe to call repeatedly.
 * @returns {Promise<boolean>} Whether the wishlist module is usable.
 */
function boot() {
  bootPromise ??= (async () => {
    const bundleUrl = findBundleUrl();
    const config = takeEmbedConfig();

    // Both are scraped out of Growave's app embed, so a change on their side lands here first.
    // Without this the buttons just stop working and nothing anywhere says why.
    if (!bundleUrl || !config) {
      console.warn(
        `Growave wishlist disabled: the app embed is missing its ${!bundleUrl ? 'bundle URL' : 'config'}. ` +
          'Check the app embed is enabled, then that wishlist.js still matches its markup.'
      );
      return false;
    }

    try {
      // The bundle is webpack-chunked and expects its global to exist; the embed's loader
      // normally sets it up just before appending the script.
      window.gwWebpackGlobal ??= {};

      // Growave copies this stylesheet into each matching widget's shadow root.
      window.gw.registerWidgetCustomization(
        ({ widgetName }) =>
          ['WishlistPageWidgetV2', 'SharedWishlistPageWidgetV2'].includes(widgetName) ||
          widgetName.includes('ModalWidget'),
        () => ({ styleElement: document.querySelector('#wishlist-card-styles') }),
      );

      await loadScript(bundleUrl, { module: true });
      const sdk = await whenSdkReady();

      // Passing `enabledApps` replaces the embed's `availableApps`, so the rewards, Instagram
      // and social-login chunks are never fetched.
      sdk.init({
        ...config,
        enabledApps: [sdk.common.constants.apps.WISHLISTS],
        localization: config.gwI18nLocale,
        myshopifyDomain: window.Shopify?.shop,
      });

      await whenWishlistsReady(sdk);
      return true;
    } catch (error) {
      console.error('Growave wishlist failed to load:', error);
      return false;
    }
  })();

  return bootPromise;
}

/**
 * @returns {any} The wishlist method namespace.
 */
function wishlistMethods() {
  return window.gw?.sdk?.wishlists?.methods;
}

/**
 * Paints a button's state. The accessible name follows `aria-pressed` so the label never
 * contradicts the state.
 * @param {HTMLElement} button
 * @param {boolean} isSaved
 */
function paintButton(button, isSaved) {
  if (!unpressedLabels.has(button)) {
    unpressedLabels.set(button, button.getAttribute('aria-label') ?? '');
  }

  button.setAttribute('aria-pressed', String(isSaved));

  const label = isSaved ? button.dataset.labelPressed : unpressedLabels.get(button);
  if (label) button.setAttribute('aria-label', label);
}

/**
 * Applies the known wishlist state to every button on the page, yielding between batches so a
 * long collection never blocks input.
 * @param {ParentNode} [root=document]
 */
async function syncButtons(root = document) {
  const buttons =
    root instanceof Element && root.matches(TOGGLE_SELECTOR) ? [root] : root.querySelectorAll(TOGGLE_SELECTOR);
  let index = 0;

  for (const button of buttons) {
    if (!(button instanceof HTMLElement)) continue;
    paintButton(button, savedProductIds.has(Number(button.dataset.productId)));

    if (++index % 50 === 0) await yieldToMainThread();
  }
}

function syncCount() {
  for (const element of document.querySelectorAll(COUNT_SELECTOR)) {
    if (!(element instanceof HTMLElement)) continue;
    const countText = element.querySelector('[data-wishlist-count-text]');
    if (!countText) throw new Error('Wishlist count text element is missing');
    countText.textContent = String(savedProductIds.size);
    element.hidden = savedProductIds.size === 0;
  }
}

/**
 * Fetches the wishlist once and paints everything from it.
 * @returns {Promise<void>}
 */
function hydrate() {
  // Once is enough, and it must be once: a second fetch can land after a toggle and repaint the
  // button with server state that predates it.
  // Reveal last: showing every product as unsaved before the state is known would be a lie.
  hydratePromise ??= fetchAndPaint().finally(() => {
    document.documentElement.dataset.wishlist = 'ready';
  });
  return hydratePromise;
}

/** @returns {Promise<void>} */
async function fetchAndPaint() {
  watchForNewButtons();

  // Growave rejects this for a shopper who has never saved anything — there is simply no list
  // yet. Nothing to paint, which is already the rendered state.
  const response = await wishlistMethods()?.getWishlistItems().catch(() => null);
  const items = response?.result;
  if (!Array.isArray(items)) return;

  savedProductIds.clear();
  for (const item of items) savedProductIds.add(Number(item.productId));

  await syncButtons();
  syncCount();
}

/**
 * Adds or removes the product, updating the UI optimistically.
 * @param {HTMLElement} button
 * @returns {Promise<void>}
 */
async function toggle(button) {
  const productId = Number(button.dataset.productId);
  const variantId = Number(button.dataset.variantId);
  if (!productId) return;

  const wasSaved = savedProductIds.has(productId);
  const buttons = document.querySelectorAll(`${TOGGLE_SELECTOR}[data-product-id="${productId}"]`);

  const paintAll = (/** @type {boolean} */ isSaved) => {
    for (const element of buttons) {
      if (element instanceof HTMLElement) paintButton(element, isSaved);
    }
  };

  savedProductIds[wasSaved ? 'delete' : 'add'](productId);
  paintAll(!wasSaved);
  syncCount();

  try {
    const methods = wishlistMethods();
    const response = wasSaved
      ? await methods.deleteWishlistItem(productId, variantId || undefined)
      : await methods.createWishlistItem(productId, variantId);

    if (response?.status === 'error') throw new Error(response.error ?? 'Wishlist update failed');
  } catch (error) {
    console.error('Wishlist update failed:', error);
    savedProductIds[wasSaved ? 'add' : 'delete'](productId);
    paintAll(wasSaved);
    syncCount();
  }
}

/** Product ids with a mutation in flight; per product, not per button, so duplicate cards share it. */
const inFlight = new Set();

/**
 * @param {HTMLElement} button
 */
async function activate(button) {
  const productId = Number(button.dataset.productId);
  if (inFlight.has(productId)) return;
  inFlight.add(productId);

  // Both calls are memoised, so this is a no-op once the page has settled. Waiting on them
  // unconditionally is what keeps an early tap from being overwritten by a hydration that was
  // already in flight when it happened.
  button.setAttribute('aria-busy', 'true');

  try {
    if (!(await boot())) return;
    await hydrate();
    await toggle(button);
  } finally {
    button.removeAttribute('aria-busy');
    inFlight.delete(productId);
  }
}

/** @param {MouseEvent} event */
function handleClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest(TOGGLE_SELECTOR);
  if (!(button instanceof HTMLElement)) return;

  event.preventDefault();
  activate(button);
}

document.addEventListener('click', handleClick);

/**
 * Filtering and section morphs swap cards in. Repainting them is local and cheap; re-running
 * the SDK init is not, so only the paint pass repeats.
 */
function watchForNewButtons() {
  if (isWatching) return;
  isWatching = true;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(TOGGLE_SELECTOR) || node.querySelector(TOGGLE_SELECTOR)) {
          syncButtons(node);
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

async function start() {
  if (await boot()) await hydrate();
}

/** Recommendations arrive after load, so a direct PDP visit has no buttons at document ready. */
function startWhenButtonsExist() {
  if (document.querySelector(TOGGLE_SELECTOR)) {
    whenDeepIdle(start);
    return;
  }

  const waiting = new MutationObserver(() => {
    if (!document.querySelector(TOGGLE_SELECTOR)) return;
    waiting.disconnect();
    whenDeepIdle(start);
  });

  waiting.observe(document.body, { childList: true, subtree: true });
}

// /pages/wishlist renders nothing until the SDK fills Growave's placeholder, so there it loads
// as soon as the DOM is ready.
onDocumentReady(() => {
  watchForNewButtons();

  if (document.querySelector(PAGE_WIDGET_SELECTOR)) start();
  else startWhenButtonsExist();
});
